import { css } from '@emotion/react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { MenuButton } from '../../../../../pages/Finance2/common-grid-defs/MenuButton';
import {
  useConsolidatedTemplatesState,
  useDeleteConsolidatedTemplate,
} from '../../../../../pages/KPI/KpiCollection/CustomKpiTemplate/state/custom-template-state';
import { IConsolidatedTemplate } from '../../../../../schemas/kpi-template.schema';
import { colors } from '../../../../../theme/colors';
import { ConfirmDeletionDialog } from '../../../../Dialog/ConfirmDialog/ConfirmDeletionDialog';
import PlusIconButton from '../../../../IconButton/PlusIconButton';
import { SubSectionNavigation } from '../../SubSectionNavigation/SubSectionNavigation';
import { SubNavItem } from '../../SubSectionNavigation/components/SubNavItem/SubNavItem';
import { SubNavSectionWrapper } from '../../SubSectionNavigation/SubSectionNavItems';

const NewTemplateId = 'new' as const;

const menuButtonCss = css`
  &&&& .MuiButtonBase-root[title='template actions'] svg {
    fill: ${colors.secondary[20]};
  }
`;

export function useCustomTemplateNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: templates = [] } = useConsolidatedTemplatesState();
  const { mutateAsync: deleteTemplate, isPending: isDeleting } = useDeleteConsolidatedTemplate();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Pick<IConsolidatedTemplate, 'id' | 'name'> | null>(
    null
  );

  const selectedTemplateId = pathname.split('/').pop();
  const isNewTemplate = selectedTemplateId === NewTemplateId;
  const parentRoute = `${ROUTES.KPI}/${ROUTES.KPI_CUSTOM_TEMPLATE_CONFIGURATION}`;

  const sections = useMemo(() => {
    const items = templates.map((t) => ({ id: String(t.id), value: t.name }));
    if (isNewTemplate) {
      return [{ id: NewTemplateId, value: 'New Template' }, ...items];
    }
    return items;
  }, [templates, isNewTemplate]);

  const handleNavigateNew = useCallback(() => {
    navigate(`/${parentRoute}/${NewTemplateId}`);
  }, [navigate, parentRoute]);

  const handleDeleteClick = useCallback((template: Pick<IConsolidatedTemplate, 'id' | 'name'>) => {
    setTemplateToDelete(template);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setTemplateToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (templateToDelete?.id) {
      await deleteTemplate(templateToDelete.id);
      const remaining = templates.filter((t) => t.id !== templateToDelete.id);
      if (remaining.length > 0) {
        navigate(`/${parentRoute}/${remaining[0].id}`);
      } else {
        // No templates left: go to the index route, which renders the empty state.
        // Do NOT navigate to /new — that would show "New Template" in the sidebar.
        navigate(`/${parentRoute}`);
      }
    }
    handleCloseDeleteModal();
  }, [templateToDelete, templates, deleteTemplate, navigate, parentRoute, handleCloseDeleteModal]);

  return {
    sections,
    templates,
    selectedTemplateId,
    isNewTemplate,
    parentRoute,
    deleteModalOpen,
    templateToDelete,
    isDeleting,
    handleNavigateNew,
    handleDeleteClick,
    handleCloseDeleteModal,
    handleConfirmDelete,
  };
}

export function CustomTemplateNav() {
  const {
    sections,
    templates,
    selectedTemplateId,
    isNewTemplate,
    parentRoute,
    deleteModalOpen,
    templateToDelete,
    isDeleting,
    handleNavigateNew,
    handleDeleteClick,
    handleCloseDeleteModal,
    handleConfirmDelete,
  } = useCustomTemplateNav();

  return (
    <>
      <ConfirmDeletionDialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        title={`Delete "${templateToDelete?.name}"?`}
      >
        <Typography variant='body2' color='text.secondary'>
          This action cannot be undone.
        </Typography>
      </ConfirmDeletionDialog>
      <SubSectionNavigation
        title='Templates'
        RightSlot={
          <PlusIconButton
            sx={{
              color: colors.secondary[20],
              '&.Mui-disabled': { color: colors.secondary[20], opacity: 0.6 },
            }}
            onClick={handleNavigateNew}
            disabled={isNewTemplate}
          >
            Add New
          </PlusIconButton>
        }
      >
        <SubNavSectionWrapper>
          {sections.map((section) => {
            const isSelected = selectedTemplateId === section.id;
            const isNew = section.id === NewTemplateId;
            const originalTemplate = templates.find((t) => String(t.id) === section.id);

            return (
              <Stack
                key={section.id}
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                css={menuButtonCss}
              >
                <SubNavItem section={section} parentRoute={parentRoute} />
                {isSelected && !isNew && originalTemplate && (
                  <MenuButton
                    menuItems={[
                      {
                        name: 'Delete',
                        icon: <DeleteOutlineIcon fontSize='small' />,
                        onClick: () =>
                          handleDeleteClick({ id: originalTemplate.id!, name: originalTemplate.name }),
                      },
                    ]}
                    label='template actions'
                  />
                )}
              </Stack>
            );
          })}
        </SubNavSectionWrapper>
      </SubSectionNavigation>
    </>
  );
}
